import React from "react";
import { t } from "@auto/services";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

import * as api from "../api";
import {
  entityTitle,
  getNumericField,
  labelOf,
  normalizeTags,
  votingCenterLabel,
} from "../model";
import {
  mergeSavedEntity,
  setError,
} from "../state";
import { canCreateGeographyEntity, canUpdateGeographyEntity } from "@src/security/permissions";

import Card from "../primitives/Card";
import EmptyState from "../primitives/EmptyState";
import Field from "../primitives/Field";
import InlineTags from "@auto/widgets/InlineTags";

import VOTING_CENTER_TAG_TYPES from "../metadata/votingcentertagtypes.mjs";

import styles from "../../ElectoralGeographyPage.module.styl";

const SelectionPanel = ({ gui, state, setState, events }) => {
  const [tagSaving, setTagSaving] = React.useState(false);
  const [statusSaving, setStatusSaving] = React.useState(false);
  const [disableBlocked, setDisableBlocked] = React.useState(null);
  const [managementSummary, setManagementSummary] = React.useState(null);

  const selectedType = state.selection.type;
  const selectedItem = state.selection.item;

  const selectedLabel =
    selectedType === "voting-center"
      ? votingCenterLabel(selectedItem)
      : labelOf(selectedItem);

  React.useEffect(() => {
    if (selectedType !== "voting-center" || !selectedItem?.gid) {
      setManagementSummary(null);
      return;
    }
    let active = true;
    setManagementSummary(null);
    api.votingCenterSummary(selectedItem.gid)
      .then((summary) => { if (active) setManagementSummary(summary); })
      .catch((err) => { if (active) setError(setState, err?.message || String(err)); });
    return () => { active = false; };
  }, [selectedType, selectedItem?.gid, setState]);

  const updateVotingCenterTags = async (nextTags) => {
    if (!canUpdateGeographyEntity(gui, "voting-center")) {
      return;
    }

    if (selectedType !== "voting-center" || !selectedItem?.gid) {
      setError(setState, t("geo.votingCenterGidRequired"));
      return;
    }

    setTagSaving(true);
    setError(setState, "");

    const payload = {
      gid: selectedItem.gid,
      tags: normalizeTags(nextTags),
    };

    const localPatch = {
      gid: selectedItem.gid,
      tags: normalizeTags(nextTags),
    };

    try {
      await api.updateEntity("voting-center", payload);
      mergeSavedEntity(setState, "voting-center", localPatch);
      events.entitySaved.publish({ entity: "voting-center", item: localPatch });
    } catch (err) {
      setError(setState, err?.message || String(err));
    } finally {
      setTagSaving(false);
    }
  };

  const updateVotingCenterEnabled = async (enabled) => {
    if (!canUpdateGeographyEntity(gui, "voting-center")) {
      return;
    }

    if (selectedType !== "voting-center" || !selectedItem?.gid) {
      return;
    }

    setStatusSaving(true);
    setError(setState, "");

    try {
      if (enabled) {
        await api.enableVotingCenter(selectedItem.gid);
        mergeSavedEntity(setState, "voting-center", {
          gid: selectedItem.gid,
          status: 1,
        });
        setManagementSummary((previous) => previous ? { ...previous, status: 1 } : previous);
        return;
      }

      const result = await api.disableVotingCenter(selectedItem.gid);

      if (!result?.disabled) {
        setDisableBlocked({
          hasVoters: result?.hasVoters === true,
          hasApplications: result?.hasApplications === true,
        });
        return;
      }

      mergeSavedEntity(setState, "voting-center", {
        gid: selectedItem.gid,
        status: 2,
      });
      setManagementSummary((previous) => previous ? { ...previous, status: 2 } : previous);
    } catch (err) {
      setError(setState, err?.message || String(err));
    } finally {
      setStatusSaving(false);
    }
  };

  const selectedCapacity = getNumericField(selectedItem, "capacity", "capacity");
  const votingCenterDisabled =
    selectedType === "voting-center" && Number(managementSummary?.status ?? selectedItem?.status) === 2;

  const disableBlockedMessage = disableBlocked?.hasVoters && disableBlocked?.hasApplications
    ? t("geo.votingCenterCannotDisableVotersAndApplications")
    : disableBlocked?.hasVoters
      ? t("geo.votingCenterCannotDisableVoters")
      : disableBlocked?.hasApplications
        ? t("geo.votingCenterCannotDisableApplications")
        : t("geo.votingCenterCannotDisableNotEmpty");

  return (
    <>
      <Card
        icon={selectedType === "voting-center" ? PlaceOutlinedIcon : LocationCityOutlinedIcon}
        title={selectedType ? entityTitle(selectedType) : t("common.selectedItem")}
        subtitle={selectedType ? selectedLabel : t("common.selectItemTree")}
      >
        {selectedItem ? (
          <>
            <div className={styles.fieldsGrid}>
              <Field label={t("common.name")} value={selectedItem.name} />
              <Field label={t("common.code")} value={selectedItem.code} />

              {selectedType === "voting-center" ? (
                <Field
                  label={t("geo.capacity")}
                  value={selectedCapacity === "" ? 3500 : selectedCapacity}
                />
              ) : null}

              {selectedType === "voting-center" ? (
                <Field label={t("common.address")} value={selectedItem.address} />
              ) : null}
            </div>

            {selectedType === "voting-center" && !canUpdateGeographyEntity(gui, "voting-center") ? (
              <Field label={t("common.status")}
                value={votingCenterDisabled ? t("common.disabled") : t("common.enabled")} />
            ) : null}

            {selectedType === "voting-center" && canUpdateGeographyEntity(gui, "voting-center") ? (
              <div className={styles.votingCenterStatusToggle}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={!votingCenterDisabled}
                      disabled={statusSaving || Boolean(managementSummary?.targetVotingCenterGid) || !canUpdateGeographyEntity(gui, "voting-center")}
                      onChange={(event) => updateVotingCenterEnabled(event.target.checked)}
                    />
                  )}
                  label={
                    votingCenterDisabled
                      ? t("common.disabled")
                      : t("common.enabled")
                  }
                />
              </div>
            ) : null}

            {selectedType === "voting-center" && managementSummary?.targetVotingCenterGid ? (
              <div className={styles.contextNote}>
                {t("geo.redirectedTo")}: <strong>{[managementSummary.targetCode, managementSummary.targetName].filter(Boolean).join(" — ")}</strong>
              </div>
            ) : null}

            {selectedType === "voting-center" ? (
              <div className={styles.tagsBlock}>
                <InlineTags
                  tags={normalizeTags(selectedItem.tags)}
                  tagTypes={VOTING_CENTER_TAG_TYPES}
                  showHeader={false}
                  emptyText=""
                  onChange={updateVotingCenterTags}
                  disabled={tagSaving || !canUpdateGeographyEntity(gui, "voting-center")}
                />
              </div>
            ) : null}
          </>
        ) : (
          <EmptyState>{t("common.nothingSelected")}</EmptyState>
        )}

        <div className={styles.actionRow}>
          {selectedType === "department" && canCreateGeographyEntity(gui, "arrondissement") ? (
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => events.createRequested.publish({
                entity: "arrondissement",
                parent: selectedItem,
                parentType: "department",
              })}
            >
              {t("geo.addArrondissement")}
            </Button>
          ) : null}

          {selectedType === "arrondissement" && canCreateGeographyEntity(gui, "commune") ? (
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => events.createRequested.publish({
                entity: "commune",
                parent: selectedItem,
                parentType: "arrondissement",
              })}
            >
              {t("geo.addCommune")}
            </Button>
          ) : null}

          {selectedType === "constituency" && canCreateGeographyEntity(gui, "commune") ? (
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => events.createRequested.publish({
                entity: "commune",
                parent: selectedItem,
                parentType: "constituency",
              })}
            >
              {t("geo.addCommune")}
            </Button>
          ) : null}

          {selectedType === "commune" && canCreateGeographyEntity(gui, "section") ? (
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => events.createRequested.publish({
                entity: "section",
                parent: selectedItem,
                parentType: "commune",
              })}
            >
              {t("geo.addSection")}
            </Button>
          ) : null}

          {selectedType === "section" && canCreateGeographyEntity(gui, "voting-center") ? (
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => events.createRequested.publish({
                entity: "voting-center",
                parent: selectedItem,
                parentType: "section",
              })}
            >
              {t("geo.addVotingCenter")}
            </Button>
          ) : null}

          {selectedType === "voting-center" && canUpdateGeographyEntity(gui, "voting-center")
            && !managementSummary?.targetVotingCenterGid ? (
            <a className={styles.redirectLaunch}
              href={`/electoral-geography/redirect?sourceGid=${encodeURIComponent(selectedItem.gid)}`}>
              {t("geo.redirect.menu")} <span aria-hidden="true">→</span>
            </a>
          ) : null}

          {selectedType && canUpdateGeographyEntity(gui, selectedType) ? (
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => events.editRequested.publish({ entity: selectedType, item: selectedItem })}
            >
              {t("common.edit")}
            </Button>
          ) : null}
        </div>
      </Card>

      <Dialog
        open={Boolean(disableBlocked)}
        onClose={() => setDisableBlocked(null)}
      >
        <DialogTitle>
          {t("geo.unableToDisableVotingCenter")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{disableBlockedMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableBlocked(null)}>{t("common.ok")}</Button>
          {selectedItem?.gid && canUpdateGeographyEntity(gui, "voting-center") ? (
            <a className={styles.redirectLaunch}
              href={`/electoral-geography/redirect?sourceGid=${encodeURIComponent(selectedItem.gid)}&mode=CLOSE`}>
              {t("geo.redirect.menu")} →
            </a>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectionPanel;
